import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle, XCircle, AlertCircle, Loader2, CreditCard, Calendar, Building2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERVAL_LABELS = { monthly: "Monthly", quarterly: "Quarterly", yearly: "Yearly" };

export default function SubscriptionPage() {
  const { token } = useParams();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [error, setError] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    axios.get(`${API}/subscription/${token}`)
      .then(r => { setSub(r.data); if (r.data.status === "cancelled") setCancelled(true); })
      .catch(() => setError("Subscription not found"))
      .finally(() => setLoading(false));
  }, [token]);

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await axios.post(`${API}/subscription/${token}/cancel`);
      setCancelled(true);
      setSub(prev => ({ ...prev, status: "cancelled" }));
    } catch (e) {
      setError("Failed to cancel. Please try again or contact support.");
    }
    setCancelling(false);
    setShowConfirm(false);
  };

  if (loading) return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center">
      <Loader2 className="w-6 h-6 text-[#00F0FF] animate-spin" />
    </div>
  );

  if (error && !sub) return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4">
      <div className="text-center">
        <AlertCircle className="w-12 h-12 text-neutral-600 mx-auto mb-4" />
        <p className="text-neutral-500">{error}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="subscription-page">
      <nav className="border-b border-white/5 bg-[#050505]/80 backdrop-blur-xl">
        <div className="max-w-lg mx-auto px-6 h-14 flex items-center">
          <span className="font-outfit font-medium text-white text-lg tracking-tight">wedowedo</span>
        </div>
      </nav>

      <div className="max-w-lg mx-auto px-6 py-16">
        <h1 className="font-outfit font-bold text-2xl text-white mb-2">Manage subscription</h1>
        <p className="text-sm text-neutral-500 mb-8">Review and manage your recurring subscription.</p>

        <div className="bg-[#0a0a0a] border border-white/5 rounded-sm p-6 space-y-5">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${cancelled ? "bg-red-500/10" : "bg-green-500/10"}`}>
              {cancelled ? <XCircle className="w-5 h-5 text-red-400" /> : <CheckCircle className="w-5 h-5 text-green-400" />}
            </div>
            <div>
              <h2 className="font-outfit font-medium text-white">{sub.title}</h2>
              <span className={`text-xs px-2 py-0.5 rounded-sm font-mono ${cancelled ? "bg-red-500/10 text-red-400" : "bg-green-500/10 text-green-400"}`}>
                {cancelled ? "Cancelled" : "Active"}
              </span>
            </div>
          </div>

          <div className="space-y-3 pt-3 border-t border-white/5">
            <div className="flex justify-between items-center">
              <span className="text-sm text-neutral-500 flex items-center gap-2"><Building2 className="w-4 h-4" /> Customer</span>
              <span className="text-sm text-white">{sub.company_name}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-neutral-500 flex items-center gap-2"><CreditCard className="w-4 h-4" /> Amount</span>
              <span className="text-sm text-white font-mono">{sub.amount?.toLocaleString("sv-SE", { minimumFractionDigits: 2 })} {sub.currency}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-neutral-500 flex items-center gap-2"><Calendar className="w-4 h-4" /> Billing interval</span>
              <span className="text-sm text-white">{INTERVAL_LABELS[sub.billing_interval] || sub.billing_interval}</span>
            </div>
            {sub.next_invoice_date && !cancelled && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-neutral-500">Next invoice</span>
                <span className="text-sm text-white">{sub.next_invoice_date}</span>
              </div>
            )}
            {sub.cancelled_at && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-neutral-500">Cancelled</span>
                <span className="text-sm text-red-400">{sub.cancelled_at.slice(0, 10)}</span>
              </div>
            )}
          </div>
        </div>

        {!cancelled && (
          <div className="mt-8">
            {!showConfirm ? (
              <button onClick={() => setShowConfirm(true)} data-testid="cancel-subscription-btn"
                className="w-full border border-red-500/30 text-red-400 py-3 rounded-sm text-sm font-medium hover:bg-red-500/10 transition-all">
                Cancel subscription
              </button>
            ) : (
              <div className="bg-[#0a0a0a] border border-red-500/20 rounded-sm p-6 text-center">
                <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-3" />
                <p className="text-white font-medium mb-1">Are you sure?</p>
                <p className="text-sm text-neutral-500 mb-4">This will stop all future automatic invoices and charges for this subscription.</p>
                <div className="flex gap-3">
                  <button onClick={() => setShowConfirm(false)}
                    className="flex-1 bg-white/5 text-white py-2.5 rounded-sm text-sm hover:bg-white/10 transition-all">
                    Keep subscription
                  </button>
                  <button onClick={handleCancel} disabled={cancelling} data-testid="confirm-cancel-btn"
                    className="flex-1 bg-red-500/20 text-red-400 border border-red-500/30 py-2.5 rounded-sm text-sm hover:bg-red-500/30 transition-all disabled:opacity-50 flex items-center justify-center gap-2">
                    {cancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    {cancelling ? "Cancelling..." : "Yes, cancel"}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {cancelled && (
          <div className="mt-8 bg-[#0a0a0a] border border-white/5 rounded-sm p-6 text-center">
            <CheckCircle className="w-8 h-8 text-neutral-600 mx-auto mb-3" />
            <p className="text-neutral-400 text-sm">This subscription has been cancelled. No further invoices or charges will be made.</p>
            <p className="text-neutral-600 text-xs mt-2">Contact us at hey@wedowedo.co if you have questions.</p>
          </div>
        )}

        {error && sub && <p className="text-red-400 text-sm mt-4 text-center">{error}</p>}
      </div>
    </div>
  );
}
